import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { z } from 'zod';
import { Alert, Box, CircularProgress } from '@mui/material';

/**
 * Runtime configuration, fetched from `/config.json` rather than baked in at build time — so the same
 * bundle can be promoted between environments, and the UI stack writes the file at deploy.
 */
const ConfigSchema = z.object({
  env: z.string(),
  authRegion: z.string(),
  graphqlEndpoint: z.string().startsWith('http'),
  authUserPoolId: z.string(),
  authUserPoolWebClientId: z.string(),
});

export type ConfigType = z.infer<typeof ConfigSchema>;

/** The delegated zone from `cdk.json` context. Kept here rather than imported: the UI is its own
 * workspace package and does not reach into the CDK app. */
const HOSTED_ZONE = 'vms.playground.amer.merapar.net';

/**
 * Where to load `config.json` from.
 *
 * Unset (the deployed case): relative, so the SPA reads the file its own UI stack published alongside
 * the bundle. Set to an environment name (`VITE_ENV_NAME=test pnpm dev`): that environment's
 * CloudFront, which serves it CORS-open on non-prod. Nothing else needs configuring for local
 * development — the endpoints, user pool and client id all come from the file.
 *
 * Dashes are stripped because `getEnvName()` in the CDK app strips them too, so the branch
 * `feature/VMS-12` deploys to `featvms12.<zone>`, not `feat-vms-12.<zone>`.
 */
const baseUrl = import.meta.env.VITE_ENV_NAME
  ? `https://${
      import.meta.env.VITE_ENV_NAME === 'prod'
        ? HOSTED_ZONE
        : `${import.meta.env.VITE_ENV_NAME.replace(/-/g, '')}.${HOSTED_ZONE}`
    }`
  : '';

export const ConfigContext = createContext<ConfigType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useConfig = () => {
  const config = useContext(ConfigContext);
  if (!config) {
    throw new Error('useConfig used outside ConfigProvider');
  }
  return config;
};

const loadConfig = async () => {
  const response = await fetch(`${baseUrl}/config.json`);
  if (!response.ok) {
    throw new Error(`config.json returned ${response.status}`);
  }
  return ConfigSchema.parse(await response.json());
};

const ConfigProvider = ({ children }: { children: ReactNode }) => {
  const [config, setConfig] = useState<ConfigType>();
  const [errorMessage, setErrorMessage] = useState<string>();

  useEffect(() => {
    loadConfig()
      .then(setConfig)
      .catch((e: unknown) => {
        setErrorMessage(e instanceof Error ? e.message : JSON.stringify(e));
      });
  }, []);

  if (errorMessage) {
    return <Alert severity="error">Config loading error: {errorMessage}</Alert>;
  }

  if (!config) {
    return (
      <Box sx={{ display: 'flex' }}>
        <CircularProgress sx={{ mx: 'auto', mt: 4 }} />
      </Box>
    );
  }

  return (
    <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>
  );
};

export default ConfigProvider;
